import {type FC, useState} from 'react';
import DeletingConfirmationDialog from '@/components/PointCloudsUploadForm/common/DeletingConfirmationDialog';
import PointCloudReuploadDialog from '@/components/PointCloudsUploadForm/common/PointCloudReuploadDialog';
import PointCloudsAddInfoButton from '@/components/PointCloudsUploadForm/common/PointCloudsAddInfoButton';
import UavPointCloudInfo from '@/components/PointCloudsUploadForm/uav/UavPointCloudInfo';
import UavPointCloudUploadingDialog from '@/components/PointCloudsUploadForm/uav/UavPointCloudUploadingDialog';
import type {LidarTechnology} from '@/domain/entities/LidarTechnology';
import type {UavPointCloud, UavPointCloudCreate} from '@/domain/entities/UavPointCloud';

type Props = {
  uavPointCloud?: UavPointCloud;
  lidarTechnologies?: LidarTechnology[];
  canUpload: boolean;
  canDelete: boolean;
  onDelete: (pointCloudUuid: string) => void;
  onUpload: (pointCloud: Partial<UavPointCloudCreate>, file: File) => void;
  onDownload: (pointCloudUuid: string) => void;
  onReupload: (pointCloudUuid: string, file: File) => void;
  disabled: boolean;
};

const UavPointCloudButton: FC<Props> = ({
  uavPointCloud,
  lidarTechnologies,
  canUpload,
  canDelete,
  onDelete,
  onUpload,
  onDownload,
  onReupload,
  disabled,
}) => {
  const [isUploadDialogOpen, setUploadDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [isInfoDialogOpen, setInfoDialogOpen] = useState<boolean>(false);
  const [isReuploadDialogOpen, setReuploadDialogOpen] = useState<boolean>(false);

  const handleDelete = () => uavPointCloud && onDelete(uavPointCloud?.uuid);
  const handleDownload = () => uavPointCloud && onDownload(uavPointCloud?.uuid);
  const handleUpload = (pointCloud: Partial<UavPointCloudCreate>, file: File) => {
    onUpload(pointCloud, file);
    setUploadDialogOpen(false);
    setInfoDialogOpen(false);
  };
  const handleReupload = (file: File) => {
    setReuploadDialogOpen(false);
    setInfoDialogOpen(false);
    uavPointCloud && onReupload(uavPointCloud?.uuid, file);
  };

  return (
    <>
      <PointCloudsAddInfoButton
        canUpload={canUpload}
        pointCloud={uavPointCloud}
        onUploadClick={() => setUploadDialogOpen(true)}
        onInfoClick={() => setInfoDialogOpen(true)}
        disabled={disabled}
      />
      {isInfoDialogOpen && uavPointCloud && (
        <UavPointCloudInfo
          canDelete={canDelete}
          canUpload={canUpload}
          uavPointCloud={uavPointCloud}
          lidarTechnologies={lidarTechnologies}
          onClose={() => setInfoDialogOpen(false)}
          onDelete={() => setDeleteDialogOpen(true)}
          onDownload={handleDownload}
          onReupload={() => setReuploadDialogOpen(true)}
          disabled={disabled}
        />
      )}
      {isDeleteDialogOpen && (
        <DeletingConfirmationDialog onClose={() => setDeleteDialogOpen(false)} onDelete={handleDelete} />
      )}
      {isUploadDialogOpen && (
        <UavPointCloudUploadingDialog
          lidarTechnologies={lidarTechnologies}
          onUploadPointCloud={handleUpload}
          onClose={() => setUploadDialogOpen(false)}
        />
      )}
      {isReuploadDialogOpen && (
        <PointCloudReuploadDialog onReupload={handleReupload} onClose={() => setReuploadDialogOpen(false)} />
      )}
    </>
  );
};

export default UavPointCloudButton;
